// iter687 — Admin Broadcasts panel.
// Compose + send platform-wide email announcements (new modules, feature
// releases, important changes) through SendGrid via /api/admin/broadcasts.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Eye, History, Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  { id: "verified",          label_bg: "Само потвърдени имейли", hint_bg: "Препоръчано" },
  { id: "all",               label_bg: "Всички регистрирани",     hint_bg: "Включително непотвърдени" },
  { id: "active_subscribers", label_bg: "Активни абонати",        hint_bg: "Само платени планове" },
  { id: "admins",            label_bg: "Само админи",             hint_bg: "За вътрешни тестове" },
];

export default function AdminBroadcastsPanel() {
  const [form, setForm] = useState({
    subject: "",
    headline_bg: "",
    body_bg: "",
    cta_label_bg: "",
    cta_url: "",
    audience: "verified",
    test_mode: false,
  });
  const [previewHtml, setPreviewHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  async function refreshHistory() {
    try {
      const { data } = await axios.get(`${API}/admin/broadcasts?limit=30`);
      setHistory(data.broadcasts || []);
    } catch {
      setHistory([]);
    }
  }
  useEffect(() => { refreshHistory(); }, []);

  async function generatePreview() {
    if (!form.subject || !form.headline_bg || !form.body_bg) {
      toast.error("Попълни subject + headline + body");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/admin/broadcasts/preview`, { params: form });
      setPreviewHtml(data.html || "");
    } catch (e) {
      toast.error("Preview грешка: " + (e?.response?.data?.detail || e.message));
    }
  }

  async function sendTest() {
    if (!testEmail) { toast.error("Дай test email"); return; }
    setTesting(true);
    try {
      await axios.post(`${API}/admin/broadcasts/test`, {
        to_email: testEmail,
        subject: form.subject,
        headline_bg: form.headline_bg,
        body_bg: form.body_bg,
        cta_label_bg: form.cta_label_bg || null,
        cta_url: form.cta_url || null,
      });
      toast.success(`Test email изпратен на ${testEmail}`);
    } catch (e) {
      toast.error("Test failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setTesting(false);
    }
  }

  async function sendBroadcast() {
    if (!form.subject || !form.headline_bg || !form.body_bg) {
      toast.error("Попълни subject + headline + body");
      return;
    }
    if (!window.confirm(
      form.test_mode
        ? "Изпрати само на твоя имейл (test mode)?"
        : `Сигурен/а ли си? Това ще изпрати имейл на ВСИЧКИ потребители в '${form.audience}'.`
    )) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/broadcasts`, {
        ...form,
        cta_label_bg: form.cta_label_bg || null,
        cta_url: form.cta_url || null,
      });
      toast.success(`Изпратени: ${data.sent_count} / ${data.recipients_total} (failed: ${data.failed_count})`);
      refreshHistory();
    } catch (e) {
      toast.error("Send failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-broadcasts-panel">
      <header>
        <h2 className="text-2xl font-bold">📣 Email Broadcasts</h2>
        <p className="text-sm text-slate-500 mt-1">
          Изпращай съобщения за нови модули и важни промени до всички потребители.
        </p>
      </header>

      {/* Compose */}
      <div className="bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-xl p-4 space-y-3">
        <div>
          <label className="text-xs font-semibold uppercase opacity-70">Subject (показва се в inbox)</label>
          <input
            value={form.subject}
            onChange={(e) => update("subject", e.target.value)}
            data-testid="bc-subject"
            placeholder="Например: Ново — Жива мрежа на близнаците"
            className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-900 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase opacity-70">Headline (заглавие вътре в имейла)</label>
          <input
            value={form.headline_bg}
            onChange={(e) => update("headline_bg", e.target.value)}
            data-testid="bc-headline"
            placeholder="Например: 🕸️ Виж как 20-те Ви близнаци говорят помежду си"
            className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-900 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase opacity-70">Body (markdown-light — двойни нови редове = нов параграф)</label>
          <textarea
            value={form.body_bg}
            onChange={(e) => update("body_bg", e.target.value)}
            data-testid="bc-body"
            rows={8}
            placeholder="Здравей,&#10;&#10;Радваме се да съобщим, че пуснахме нов модул..."
            className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-900 text-sm font-mono"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase opacity-70">CTA текст (по избор)</label>
            <input
              value={form.cta_label_bg}
              onChange={(e) => update("cta_label_bg", e.target.value)}
              data-testid="bc-cta-label"
              placeholder="Например: Влез в Nutramilo"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-900 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase opacity-70">CTA URL (по избор)</label>
            <input
              value={form.cta_url}
              onChange={(e) => update("cta_url", e.target.value)}
              data-testid="bc-cta-url"
              placeholder="https://nutramilo.com/twins/network"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-900 text-sm"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase opacity-70">Аудитория</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
            {AUDIENCES.map((a) => (
              <label key={a.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${
                form.audience === a.id ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/15" : "border-slate-200 dark:border-slate-700"
              }`}>
                <input
                  type="radio"
                  name="bc-audience"
                  data-testid={`bc-audience-${a.id}`}
                  checked={form.audience === a.id}
                  onChange={() => update("audience", a.id)}
                />
                <div className="text-sm">
                  <div className="font-semibold">{a.label_bg}</div>
                  <div className="text-[10px] opacity-60">{a.hint_bg}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={generatePreview}
            data-testid="bc-preview-btn"
            className="px-3 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-sm font-semibold flex items-center gap-1.5"
          >
            <Eye className="w-4 h-4" /> Преглед
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              data-testid="bc-test-email"
              placeholder="test@example.com"
              className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-900 text-sm w-48"
            />
            <button
              onClick={sendTest}
              disabled={testing}
              data-testid="bc-test-btn"
              className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Test send
            </button>
          </div>
          <button
            onClick={sendBroadcast}
            disabled={sending}
            data-testid="bc-send-btn"
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-rose-500 to-indigo-500 hover:from-rose-600 hover:to-indigo-600 text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Изпрати broadcast →
          </button>
        </div>
      </div>

      {/* Preview */}
      {previewHtml && (
        <div className="bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-xl p-3" data-testid="bc-preview-box">
          <div className="text-xs uppercase font-bold opacity-70 mb-2">Preview</div>
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white">
            <iframe
              title="broadcast-preview"
              srcDoc={previewHtml}
              className="w-full"
              style={{ height: 620, border: 0 }}
            />
          </div>
        </div>
      )}

      {/* History */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold flex items-center gap-1.5"><History className="w-4 h-4" /> История</h3>
          <button onClick={refreshHistory} className="text-xs flex items-center gap-1 opacity-70 hover:opacity-100">
            <RefreshCw className="w-3 h-3" /> опресни
          </button>
        </div>
        <div className="space-y-2">
          {history.length === 0 && <p className="text-sm opacity-60">Все още няма изпратени broadcast-и.</p>}
          {history.map((b, i) => (
            <div
              key={i}
              data-testid={`bc-history-${i}`}
              className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-sm">{b.subject}</div>
                <div className="text-[10px] opacity-50 whitespace-nowrap">{(b.created_at || "").substring(0, 16).replace("T", " ")}</div>
              </div>
              <div className="text-xs opacity-70 mt-0.5">{b.headline_bg}</div>
              <div className="text-[10px] opacity-50 mt-1 flex gap-3">
                <span>аудитория: <strong>{b.audience}</strong></span>
                <span>изпратени: <strong className="text-emerald-500">{b.sent_count}</strong> / {b.recipients_total}</span>
                {b.failed_count > 0 && <span className="text-rose-500">failed: {b.failed_count}</span>}
                {b.test_mode && <span className="text-amber-500">[TEST]</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
